import styled from '@emotion/styled';
import { type FC, type PropsWithChildren, useCallback, useEffect, useState } from 'react';

import BodyEnd from 'app/components/v2/components/BodyEnd';
import ClickAwayListener from 'app/components/v2/components/EventListeners/ClickAwayListener';
import KeyCodeListener from 'app/components/v2/components/EventListeners/KeyCodeListener';
import styles from 'app/components/v2/constants/styles';
import { EvtKeys } from 'app/types';

import { globalSearchInputMaxWidth } from '../constants';

interface DropdownContainerProps {
  boundingClientRect: DOMRect;
  offset: number;
}

export interface GlobalSearchDropdownProps {
  anchorEl: HTMLElement;
  isOpen: boolean;
  offset?: number;
  onClose: () => void;
}

const DropdownContainer = styled.div<DropdownContainerProps>(({
  boundingClientRect,
  offset,
}) => {
  const viewportPadding = 16;
  const availableWidth = window.innerWidth - viewportPadding * 2;
  const dropdownWidth = Math.min(globalSearchInputMaxWidth, availableWidth);

  const inputCenter = boundingClientRect.left + boundingClientRect.width / 2;
  const idealLeft = inputCenter - dropdownWidth / 2;
  const minLeft = viewportPadding;
  const maxLeft = window.innerWidth - dropdownWidth - viewportPadding;
  const left = Math.max(minLeft, Math.min(idealLeft, maxLeft));

  return {
    borderRadius: styles.borderRadius.md,
    boxShadow: styles.boxShadow.default,
    left,
    maxHeight: window.innerHeight * 0.8,
    overflowY: 'auto',
    position: 'fixed',
    top: boundingClientRect.bottom + offset,
    width: dropdownWidth,
    zIndex: styles.zIndex.modal,
  };
});

const GlobalSearchDropdown: FC<PropsWithChildren<GlobalSearchDropdownProps>> = ({
  anchorEl,
  children,
  isOpen,
  offset = 10,
  onClose,
}) => {
  const [boundingRect, setBoundingRect] = useState<DOMRect>(() => anchorEl.getBoundingClientRect());

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setBoundingRect(anchorEl.getBoundingClientRect());
      }, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [anchorEl]);

  if (!isOpen) {
    return null;
  }

  return (
    <BodyEnd>
      <KeyCodeListener
        keyCodeActions={[
          {
            action: handleClose,
            key: EvtKeys.Esc,
          },
        ]}
      >
        <ClickAwayListener onClickOutside={handleClose}>
          <DropdownContainer
            boundingClientRect={boundingRect}
            data-testid="global-search-dropdown"
            offset={offset}
          >
            {children}
          </DropdownContainer>
        </ClickAwayListener>
      </KeyCodeListener>
    </BodyEnd>
  );
};

export default GlobalSearchDropdown;
